// [목적] POI 혼잡도 예측 / 연관 POI 조회 API 클라이언트 함수 — fetchWithEnvelope + zod 파싱
// [이유] WC-TOURAPI-QR-5-04 — GET /pois/:id/congestion, GET /pois/:id/related 두 엔드포인트를
//         RTK Query 없이 fetcher 레이어로 분리. usePoiCongestion / usePoiRelated 훅이 이 함수를 래핑한다.
//         기존 프로젝트가 TanStack Query + fetchWithEnvelope 패턴을 채택하고 있어 동일 패턴 유지
// [사이드 이팩트] fetchWithEnvelope 경유로 401 single-flight refresh, CSRF retry, envelope unwrap 자동 처리.
//                 zod parse 실패 시 ZodError throw → useQuery error 분기 → ErrorState 폴백
// [수정 시 고려사항] BE 응답 스키마 변경 시 packages/types/tourapi.ts 의 zod 스키마와 동시 갱신.
//                   poiId 타입(uuid)을 ulid 로 변경할 경우 tourapi.ts ragCandidateMetaSchema 도 함께 갱신 필요.
//                   days 기본값 30, n 기본값 20 은 BE 엔드포인트 default 와 일치 유지

import {
  congestionForecastSchema,
  poiRelatedListSchema,
  type CongestionForecast,
  type PoiRelatedList,
} from '@gangwoncanvas/types';

import { fetchWithEnvelope } from '../api/http.js';

// [목적] GET /pois/:id/congestion?days=N — 혼잡도 예측 시계열 조회
// [이유] WC-TOURAPI-QR-5-04 — CongestionForecastCard 가 30일 곡선 렌더링에 사용.
//         zod parse 로 BE 환각값(소수 rate, 날짜 형식 이탈) 즉시 차단
// [사이드 이팩트] 없음 (read-only)
// [수정 시 고려사항] days=0 전달 시 BE 400 반환 가능 — 호출측에서 양수 보장 필요
export async function getPoiCongestion(poiId: string, days = 30): Promise<CongestionForecast> {
  const data = await fetchWithEnvelope<unknown>(`/pois/${poiId}/congestion?days=${days}`);
  return congestionForecastSchema.parse(data);
}

// [목적] GET /pois/:id/related?n=N — co-visit 기반 연관 POI 목록 조회
// [이유] WC-TOURAPI-QR-5-04 — plan-generate Fork 추천 및 "같은 코스를 다르게 즐긴 폴리오" 섹션에 제공.
//         zod parse 로 score/rank 범위 이탈 차단
// [사이드 이팩트] 없음 (read-only)
// [수정 시 고려사항] n 상한 50 은 BE 쿼리 cap 과 동기화 — tourapi.ts dayPlanCoVisitInputSchema 주석 참고
export async function getPoiRelated(poiId: string, n = 20): Promise<PoiRelatedList> {
  const data = await fetchWithEnvelope<unknown>(`/pois/${poiId}/related?n=${n}`);
  return poiRelatedListSchema.parse(data);
}
